import type { EpisodeView } from '@zyx/shared';
import { useQuery } from '@tanstack/react-query';
import type { CSSProperties } from 'react';
import { useApi } from '@/hooks/use-connection';
import { formatRuntime } from '@/lib/format';
import { parseThumbnailVtt, type SpriteTile } from '@/lib/vtt';

/** Tiles shown at most; a long movie is sampled evenly rather than shown whole. */
const MAX_TILES = 48;

interface SpriteSize {
  readonly width: number;
  readonly height: number;
}

/** The sprite's real size, from the image itself: a short clip leaves unused space, so the tiles alone understate it. */
function loadSize(url: string): Promise<SpriteSize> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
    image.onerror = () => reject(new Error('The thumbnail sprite did not load.'));
    image.src = url;
  });
}

/** An episode's thumbnail tiles in its first sprite, and that sprite's size; shared by every view of the same files. */
export function useSpriteTiles(files: { readonly previewVtt?: string; readonly previewSprite?: string }) {
  const api = useApi();
  const { previewVtt, previewSprite } = files;
  return useQuery({
    queryKey: ['scene-strip', previewVtt, previewSprite],
    enabled: previewVtt !== undefined && previewSprite !== undefined,
    staleTime: Infinity,
    queryFn: async () => {
      const [text, size] = await Promise.all([api.getAssetText(previewVtt ?? ''), loadSize(api.assetUrl(previewSprite ?? ''))]);
      const tiles = parseThumbnailVtt(text);
      const first = tiles[0]?.file;
      return { tiles: tiles.filter((tile) => tile.file === first), size };
    },
  });
}

/** CSS that crops one tile out of the sprite with background percentages, so it scales to its box. */
export function tileStyle(tile: SpriteTile, size: SpriteSize, spriteUrl: string): CSSProperties {
  const percent = (offset: number, length: number, total: number) => (total <= length ? 0 : (offset / (total - length)) * 100);
  return {
    aspectRatio: `${tile.width} / ${tile.height}`,
    backgroundImage: `url("${spriteUrl}")`,
    backgroundSize: `${(size.width / tile.width) * 100}% ${(size.height / tile.height) * 100}%`,
    backgroundPosition: `${percent(tile.x, tile.width, size.width)}% ${percent(tile.y, tile.height, size.height)}%`,
  };
}

/** The tile showing a moment: the last one starting at or before it. */
export function tileAt(tiles: ReadonlyArray<SpriteTile>, positionS: number): SpriteTile | undefined {
  let found: SpriteTile | undefined;
  for (const tile of tiles) {
    if (tile.startS > positionS) {
      break;
    }
    found = tile;
  }
  return found ?? tiles[0];
}

/**
 * The episode's scrubbing thumbnails as a grid of scenes, cropped from the cached sprite
 * with CSS background percentages, so each tile scales to its cell. Only tiles in the
 * first sprite are shown, since that is the one sprite the episode carries a hash for.
 */
export function SceneStrip(props: { readonly episode: EpisodeView; readonly onSelect?: (startS: number) => void }) {
  const api = useApi();
  const { previewSprite } = props.episode;
  const strip = useSpriteTiles(props.episode);
  if (previewSprite === undefined || strip.data === undefined || strip.data.tiles.length === 0) {
    return null;
  }
  const { tiles, size } = strip.data;
  const step = Math.max(1, Math.ceil(tiles.length / MAX_TILES));
  const shown = tiles.filter((_tile, index) => index % step === 0);
  const sprite = api.assetUrl(previewSprite);

  return (
    <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-4 md:grid-cols-6 xl:grid-cols-8">
      {shown.map((tile) => (
        <button
          type="button"
          key={tile.startS}
          onClick={() => props.onSelect?.(tile.startS)}
          aria-label={`Play from ${formatRuntime(tile.startS) ?? '0:00'}`}
          disabled={props.onSelect === undefined}
          className="group relative overflow-hidden rounded bg-muted ring-offset-background hover:ring-2 hover:ring-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:hover:ring-0"
          style={tileStyle(tile, size, sprite)}
        >
          <span className="absolute bottom-0.5 right-0.5 rounded bg-black/70 px-1 font-mono text-[10px] text-white opacity-0 transition-opacity group-hover:opacity-100">
            {formatRuntime(tile.startS) ?? '0:00'}
          </span>
        </button>
      ))}
    </div>
  );
}
